import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToken } from '../contexts/TokenContext';
import TokenWallets from './TokenWallets';

const Navbar = () => {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { hasToken, selectedToken, tokenId } = useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Generate username from first name and last name
  const getDisplayName = () => {
    if (!currentUser) return '';
    
    if (currentUser.first_name && currentUser.last_name) {
      return `${currentUser.first_name} ${currentUser.last_name}`;
    } else if (currentUser.first_name) {
      return currentUser.first_name;
    } else if (currentUser.email) {
      // If no name is available, use the part of email before @
      return currentUser.email.split('@')[0];
    }
    
    return 'User';
  };

  // Check if we're on the token selection page (root)
  const isOnTokenSelection = location.pathname === '/';

  return (
    <nav className="relative bg-white dark:bg-gray-900 shadow h-14 xs:h-16 border-b border-gray-200 dark:border-gray-700 transition-colors">
      <div className="w-full mx-auto px-2 xs:px-3 sm:px-4 md:px-6 h-full" style={{maxWidth: 'min(100%, 1792px)'}}>
        <div className="flex justify-between items-center h-full">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="Market Maker"
                className="h-8 w-8 lg:mr-3"
              />
              <span className="hidden lg:inline text-xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Market Maker
              </span>
            </Link>
          </div>
          
          <div className="flex items-center">
            {isAuthenticated ? (
              <div className="flex items-center">
                {/* Mobile menu button */}
                <button
                  onClick={toggleMobileMenu}
                  className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
                <div className="hidden lg:flex lg:space-x-1">
                  {/* Hide navigation only on token selection page */}
                  {!isOnTokenSelection && hasToken && (
                    <>
                      <Link
                        to={`/dashboard/${tokenId}`}
                        className={`lg:px-3 py-2 rounded-md lg:text-sm font-medium transition-colors whitespace-nowrap ${
                          location.pathname.startsWith('/dashboard')
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                        }`}
                      >
                        Dashboard
                      </Link>

                      <Link
                        to={`/ai-trading/${tokenId}`}
                        className={`lg:px-3 py-2 rounded-md lg:text-sm font-medium transition-colors whitespace-nowrap ${
                          location.pathname.startsWith('/ai-trading')
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                        }`}
                      >
                        <span className="md:inline lg:hidden">AI Trade</span>
                        <span className="md:hidden lg:inline">AI Trading</span>
                      </Link>

                      <Link
                        to={`/wallets/${tokenId}`}
                        className={`lg:px-3 py-2 rounded-md lg:text-sm font-medium transition-colors whitespace-nowrap ${
                          location.pathname.startsWith('/wallets')
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                        }`}
                      >
                        <span className="md:inline lg:hidden">Wallets</span>
                        <span className="md:hidden lg:inline">Wallet Groups</span>
                      </Link>

                      <Link
                        to={`/solana-distribution/${tokenId}`}
                        className={`lg:px-3 py-2 rounded-md lg:text-sm font-medium transition-colors whitespace-nowrap ${
                          location.pathname.startsWith('/solana-distribution')
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                        }`}
                      >
                        <span className="lg:inline xl:hidden">SOL Dist</span>
                        <span className="lg:hidden xl:inline">SOL Distribution</span>
                      </Link>

                      <Link
                        to={`/token-distribution/${tokenId}`}
                        className={`lg:px-3 py-2 rounded-md lg:text-sm font-medium transition-colors whitespace-nowrap ${
                          location.pathname.startsWith('/token-distribution')
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                        }`}
                      >
                        <span className="lg:inline xl:hidden">Token Dist</span>
                        <span className="lg:hidden xl:inline">Token Distribution</span>
                      </Link>

                    </>
                  )}
                </div>
                
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors lg:mx-2"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    // Sun icon for light mode
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    // Moon icon for dark mode
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
                
                {/* Token Wallets - hidden on mobile */}
                <div className="hidden lg:block">
                  <TokenWallets />
                </div>

                <div className="hidden lg:flex items-center border-l border-gray-200 dark:border-gray-700 lg:ml-4 lg:pl-4">
                  <div className="flex items-center">
                    <span className="text-gray-700 dark:text-gray-300 lg:text-sm font-medium lg:mr-3">
                      Hello, <span className="text-purple-700 dark:text-purple-400">{getDisplayName()}</span>
                    </span>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="lg:px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm font-medium lg:text-sm"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                {/* Dark Mode Toggle for non-authenticated users */}
                <button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
                
                <Link 
                  to="/login" 
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm font-medium"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isAuthenticated && isMobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 z-50 lg:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="px-4 py-3 space-y-3">
            {/* User greeting */}
            <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
              Hello, <span className="font-medium text-purple-700 dark:text-purple-400">{getDisplayName()}</span>
            </div>

            {/* Token Wallet for mobile */}
            <div className="px-4">
              <TokenWallets showAddressOnMobile={true} />
            </div>

            {/* Navigation Links */}
            {!isOnTokenSelection && hasToken && (
              <div className="space-y-1">
                <Link
                  to={`/dashboard/${tokenId}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/dashboard')
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  Dashboard
                </Link>

                <Link
                  to={`/ai-trading/${tokenId}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/ai-trading')
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  AI Trading
                </Link>

                <Link
                  to={`/wallets/${tokenId}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/wallets')
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  Wallet Groups
                </Link>

                <Link
                  to={`/solana-distribution/${tokenId}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/solana-distribution')
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  SOL Distribution
                </Link>

                <Link
                  to={`/token-distribution/${tokenId}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/token-distribution')
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  Token Distribution
                </Link>
              </div>
            )}

            {/* Logout button */}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm font-medium text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;